import React, { useCallback, useState } from 'react';

import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';

import AddFacilityForm from './AddFacilityForm';
import ValidationException from '../../exceptions/ValidationException';
import FacilityService from '../../services/facility.service';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
      margin: '16px',
      maxWidth: '500px',
      overflow: 'visible',
      width: '100%',
    },
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '36px 24px',
  },
}));

const AddFacilityModal = ({ open, onClose, onAdd, initialData = null, isEditing = false }) => {
  const classes = useStyles();
  const [validationErrors, setValidationErrors] = useState(null);
  const [defaultData, setDefaultData] = useState(initialData || {});
  const [resetData, setResetData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const facilityService = new FacilityService();

  const addFacility = async (facilityData) => {
    try {
      setIsSubmitting(true);

      const result = isEditing ? await facilityService.updateFacility(initialData.id, facilityData) : await facilityService.createFacility(facilityData);

      if (onAdd) {
        onAdd(result);
      }

      enqueueSnackbar(`Facility ${isEditing ? 'updated' : 'added'} successfully`, { variant: 'success' });

      return result;
    } catch (error) {
      if (error.response?.status === 422) {
        throw new ValidationException(error.response.data.errors);
      }

      const errorMessage = error.response?.data?.message || error.message || `Failed to ${isEditing ? 'update' : 'add'} facility`;
      enqueueSnackbar(errorMessage, { variant: 'error' });

      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAddFacility = async (formData) => {
    try {
      setResetData(false);

      await addFacility(formData);

      setDefaultData({});
      setValidationErrors(null);
      setResetData(true);
      onClose();
    } catch (exception) {
      if (exception instanceof ValidationException) {
        setValidationErrors(exception.errors);
      }
    }
  };

  const handleSetExternalError = useCallback((fieldName, value) => {
    setValidationErrors((errors) => ({ ...(errors || {}), [fieldName]: value }));
  }, []);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <div className={classes.modalContent}>
        <AddFacilityForm
          resetData={resetData}
          onAddFacility={onAddFacility}
          validationErrors={validationErrors}
          defaultData={defaultData}
          isSubmitting={isSubmitting}
          onSetExternalError={handleSetExternalError}
          onCancel={onClose}
          title={isEditing ? 'Edit Facility' : 'Add New Facility'}
          subtitle={isEditing ? 'Edit the facility information' : 'Add a new medical facility to manage departments and locations'}
          isEditing={isEditing}
        />
      </div>
    </Dialog>
  );
};

export default AddFacilityModal;
